/**
 * 13thav's own chrome: paper + ink. A shop's kit is three brand colors — primary (buttons, links,
 * highlights), secondary (the deep background of its billboards and hero), tertiary (tags, kickers).
 * Type is never a brand color: `on` / `onSecondary` are black or white, whichever reads.
 */
export const MALL_THEME = { primary: "#0B0B0B", secondary: "#27272A", tertiary: "#A1A1AA" };
export const INK = "#0B0B0B";   // the color of a dark block — a sign hanging in a daytime mall
export const PAPER = "#FFFFFF";
export const DEFAULT_THEME = MALL_THEME;
export const HOUSE_SLUG = "13th-ave-sign-and-shirt";
export const HOUSE_GREEN = "#2563EB";

const rgbOf = (hex) => {
    const h = (hex || "#0B0B0B").replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const lum = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

/** A shop's color, darkened until it reads as type on paper (neon stays for solid fills). */
export const inkOf = (hex) => {
    let c = rgbOf(hex);
    let guard = 0;
    while (lum(c) > 0.32 && guard++ < 24) c = c.map((v) => Math.round(v * 0.88));
    return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

/** Black or white — whichever is legible on this color. */
export const readableOn = (hex) => (lum(rgbOf(hex)) > 0.55 ? "#0B0B0B" : "#FFFFFF");

export const themeOf = (store) => {
    const raw = store?.theme || {};
    const t = { ...MALL_THEME, ...raw };
    // shops saved before the kit existed carry a text color in `secondary` and an `accent`
    const legacy = !raw.tertiary && ["#ffffff", "#0b0b0b", "#000000"].includes((raw.secondary || "").toLowerCase());
    const secondary = legacy ? inkOf(t.primary) : t.secondary;
    const tertiary = raw.tertiary || raw.accent || MALL_THEME.tertiary;
    return {
        primary: t.primary, secondary, tertiary,
        accent: tertiary,
        on: readableOn(t.primary), onSecondary: readableOn(secondary),
        ink: inkOf(t.primary),
        // what this accent becomes on a dark block — 13thav's own ink turns into paper white
        neon: t.primary === MALL_THEME.primary ? "#FFFFFF" : t.primary,
    };
};

/** #RRGGBB + alpha → rgba() string. */
export const tint = (hex, a = 0.12) => {
    const h = (hex || MALL_THEME.primary).replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const RANK = { department: 1, storefront: 2, kiosk: 3 };

/** The shop you walked into leads every catalog; anchors next, then the rest. */
export const sortStoreFirst = (rows = [], slug) =>
    rows.map((r, i) => ({ r, i }))
        .sort((a, b) => {
            const rank = (x) => (slug && x.r.store?.slug === slug ? 0 : RANK[x.r.store?.tier] || 4);
            return rank(a) - rank(b) || a.i - b.i;
        })
        .map(({ r }) => r);

/** Other shops stay on the page — they just stop competing. */
export const receded = (row, slug) =>
    slug && row?.store?.slug && row.store.slug !== slug
        ? "opacity-60 saturate-[0.6] transition-all duration-300 hover:opacity-100 hover:saturate-100"
        : "";

/** Which shop's bag does this cart line belong in? */
export const bagSlugOf = (item) => item?.selections?.store_slug || item?.store?.slug || HOUSE_SLUG;

/** One bag per shop — you pay one register at a time. */
export const bagsOf = (items = []) => {
    const map = new Map();
    items.forEach((it) => {
        const slug = bagSlugOf(it);
        if (!map.has(slug)) map.set(slug, { slug, store: it.store || null, items: [] });
        const bag = map.get(slug);
        if (!bag.store && it.store) bag.store = it.store;
        bag.items.push(it);
    });
    return [...map.values()].map((bag) => ({
        ...bag,
        tier: bag.store?.tier || (bag.slug === HOUSE_SLUG ? "department" : "storefront"),
        name: bag.store?.name || (bag.slug === HOUSE_SLUG ? "13th Ave" : bag.slug),
        theme: themeOf(bag.store),
        count: bag.items.reduce((n, i) => n + (i.quantity || 1), 0),
        subtotal: bag.items.reduce((s, i) => s + (i.line_total || 0), 0),
    })).sort((a, b) => (RANK[a.tier] || 4) - (RANK[b.tier] || 4) || a.name.localeCompare(b.name));
};

/** A cart from ONE shop paints the register in that shop's colors; the mall stays white. */
export const cartTheme = (items = []) => {
    const slugs = new Set(items.map(bagSlugOf));
    if (slugs.size !== 1) return MALL_THEME;
    return themeOf(items.find((i) => i.store?.theme)?.store);
};

/** The shop you're standing in paints its own products; every other shop stays mall-neutral. */
export const cardTheme = (row, activeStore) =>
    activeStore?.slug && (row?.store?.slug || row?.slug) === activeStore.slug
        ? themeOf(activeStore)
        : MALL_THEME;
